<script setup lang="ts">
// Types
import type { IMembershipSubscription } from "@citizenm/membership/shared/types";

const {
  logo,
  description,
  features,
  variant = MEMBERSHIP_COMPARE_DISPLAY_VARIANT.default,
} = defineProps<
  IMembershipSubscription & {
    variant: TMembershipCompareDisplayVariant;
  }
>();

const normalizeIconName = (name: string) => `citizenm:${slugify(name)}`;

const listItemSize = computed(() =>
  variant === MEMBERSHIP_COMPARE_DISPLAY_VARIANT.checkout
    ? ELEMENT_SIZE.medium
    : ELEMENT_SIZE.large,
);
</script>

<template>
  <div class="membership-compare-subscription">
    <div
      v-if="logo?.fields.file?.url"
      class="membership-compare-subscription__logo-wrapper"
      data-testid="membership-compoare-subscription-logo"
    >
      <NuxtImg
        :src="logo.fields.file?.url"
        :alt="logo.fields.title"
        provider="contentful"
        loading="lazy"
      />
    </div>
    <div class="membership-compare-subscription__text">
      <ContentfulNodeRenderer
        v-if="description?.content"
        :nodes="description.content"
      />
    </div>
    <div
      v-if="Array.isArray(features) && features.length"
      class="membership-compare-subscription__features"
    >
      <FoundationList>
        <FoundationListItem
          v-for="(feature, index) in features"
          :key="index"
          :bold="variant === MEMBERSHIP_COMPARE_DISPLAY_VARIANT.default"
          :size="listItemSize"
        >
          <template #start
            ><Icon :name="normalizeIconName(feature.icon)"
          /></template>
          {{ feature.label }}
        </FoundationListItem>
      </FoundationList>
    </div>
  </div>
</template>

<style scoped lang="scss">
.membership-compare-subscription {
  flex: 1 1 auto;
  color: inherit;
  container-type: inline-size;

  &__logo-wrapper {
    display: flex;
    align-items: center;
    height: 3.5rem;

    img {
      width: 75%;
      max-width: 15rem;

      @container (width > 25rem) {
        width: 50%;
      }
    }
  }

  &__features {
    margin-block-start: var(--cm-spacing-6, 1.5rem);
  }

  &__text {
    @container (width > 25rem) {
      margin-block: var(--cm-spacing-6, 1.5rem);
    }
  }

  @container (width > 25rem) {
    :deep(.foundation-list) {
      padding-left: var(--cm-spacing-4, 1rem);
    }
  }
}
</style>
